import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, RefreshCw, AlertCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stockInfo = {
  LEXX: { name: "Lexeria Biosciences", color: "blue" },
  LOBEF: { name: "Lobe Sciences", color: "purple" },
  ROMJF: { name: "Rubicon Organics", color: "green" },
};

export const StockTicker = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchQuotes = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const response = await axios.get(`${API}/stocks`);
      setQuotes(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Failed to fetch stock quotes:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
    // Refresh every 5 minutes
    const interval = setInterval(() => fetchQuotes(), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatPrice = (price) => {
    if (!price || price === 0) return "—";
    return `$${price.toFixed(4)}`;
  };

  const formatChange = (change, changePercent) => {
    if (change === null || change === undefined) return null;
    const isPositive = change >= 0;
    return {
      value: change.toFixed(4),
      percent: changePercent?.replace("%", "") || "0",
      isPositive,
    };
  };

  const formatVolume = (volume) => {
    if (!volume) return "—";
    if (volume >= 1000000) return `${(volume / 1000000).toFixed(2)}M`;
    if (volume >= 1000) return `${(volume / 1000).toFixed(1)}K`;
    return volume.toString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="stock-ticker" className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-amber-400 uppercase tracking-[0.3em] text-xs mb-1">
            Live Market Data
          </p>
          <h3 className="text-white text-lg">Public Holdings</h3>
        </div>
        <div className="flex items-center gap-4">
          {lastRefresh && (
            <span className="text-slate-600 text-xs">
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => fetchQuotes(true)}
            disabled={refreshing}
            className="p-2 text-slate-500 hover:text-amber-400 transition-colors disabled:opacity-50"
            title="Refresh quotes"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Stock Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {quotes.map((quote, index) => {
          const info = stockInfo[quote.symbol] || { name: quote.symbol, color: "slate" };
          const change = formatChange(quote.change, quote.change_percent);
          const hasError = quote.error;
          const hasData = quote.price && quote.price > 0;

          return (
            <motion.div
              key={quote.symbol}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              data-testid={`stock-card-${quote.symbol}`}
              className="glass-card p-5 relative overflow-hidden"
            >
              {/* Accent line */}
              <div
                className={`absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r ${
                  info.color === "blue"
                    ? "from-blue-500 to-blue-400"
                    : info.color === "purple"
                    ? "from-purple-500 to-purple-400"
                    : "from-green-500 to-green-400"
                }`}
              />

              {/* Symbol & Name */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className="text-amber-400 font-bold text-sm tracking-wider">
                    {quote.symbol}
                  </span>
                  <p className="text-slate-500 text-xs mt-0.5">{info.name}</p>
                </div>
                {hasData && change && (
                  <div
                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                      change.isPositive
                        ? "bg-green-500/10 text-green-400"
                        : "bg-red-500/10 text-red-400"
                    }`}
                  >
                    {change.isPositive ? (
                      <TrendingUp size={12} />
                    ) : (
                      <TrendingDown size={12} />
                    )}
                    {change.percent}%
                  </div>
                )}
              </div>

              {/* Price */}
              {hasError ? (
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <AlertCircle size={14} />
                  <span className="truncate">{quote.error}</span>
                </div>
              ) : hasData ? (
                <>
                  <p className="text-white text-2xl font-bold mb-2">
                    {formatPrice(quote.price)}
                  </p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">
                      Vol: {formatVolume(quote.volume)}
                    </span>
                    {change && (
                      <span
                        className={change.isPositive ? "text-green-400" : "text-red-400"}
                      >
                        {change.isPositive ? "+" : ""}
                        {change.value}
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-slate-500 text-sm">No data available</p>
              )}

              {/* Last updated */}
              {quote.last_updated && (
                <p className="text-slate-600 text-xs mt-3">
                  As of {quote.last_updated}
                </p>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Disclaimer */}
      <p className="text-slate-600 text-xs mt-4 text-center">
        Data provided by Yahoo Finance. Prices may be delayed up to 15 minutes.
      </p>
    </div>
  );
};
